import { useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { CloudArrowDown, Factory } from '@phosphor-icons/react'
import { useNetworkStatus } from '@/api'
import { useFormat, useI18n, type MessageKey } from '@/i18n'
import { env } from '@/lib/env'
import { downloadCaches, storeCacheAge } from '@/offline'
import { Banner, BilingualLabel, Button, EmptyState, Skeleton, useToast } from '@/ui'
import { ScreenTitle } from '@/features/phone/components/ScreenTitle'
import type { ProcessState } from '../projection'
import { useNow, useProcessState } from '../useProcess'

/** "Update site list" — the site's store list and its machines, runs and pack runs. */
export function SiteUpdateButton({ variant = 'secondary' }: { variant?: 'secondary' | 'primary' }) {
  const { t } = useI18n()
  const toast = useToast()
  const { online } = useNetworkStatus()
  const [busy, setBusy] = useState(false)
  return (
    <Button
      variant={variant}
      size="touch"
      leadingIcon={<CloudArrowDown aria-hidden="true" />}
      loading={busy}
      disabled={!online}
      onClick={() => {
        setBusy(true)
        downloadCaches()
          .then(() => toast.success(t('process.cache.updated')))
          .catch((e: unknown) =>
            toast.error(t('process.cache.failed'), e instanceof Error ? e.message : undefined),
          )
          .finally(() => setBusy(false))
      }}
    >
      {online ? t('process.cache.update') : t('process.cache.needsNetwork')}
    </Button>
  )
}

function CacheAge({ downloadedAt }: { downloadedAt: number }) {
  const { t } = useI18n()
  const fmt = useFormat()
  const now = useNow()
  const age = storeCacheAge(downloadedAt, env.rosterMaxAgeDays, now)
  const when = fmt.relative(downloadedAt)
  if (age === 'fresh')
    return <p className="text-[12px] text-muted">{t('process.cache.age', { when })}</p>
  return (
    <Banner
      tone={age === 'expired' ? 'error' : 'warn'}
      title={t(age === 'expired' ? 'process.cache.expired' : 'process.cache.stale')}
      action={<SiteUpdateButton />}
    >
      {t('process.cache.age', { when })}
    </Banner>
  )
}

export interface ProcessScreenProps {
  title: MessageKey
  children: (state: ProcessState) => ReactNode
  aside?: ReactNode
  /** Where Back goes (default: history back). */
  backTo?: string
}

/** Shell for every process and pack-hall phone screen: back, bilingual title, list age, flow. */
export function ProcessScreen({ title, children, aside, backTo }: ProcessScreenProps) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const { loading, state } = useProcessState()
  return (
    <div className="flex flex-col gap-4">
      <ScreenTitle
        title={title}
        sub={state?.pack.site_name}
        aside={aside}
        onBack={() => (backTo ? navigate(backTo) : navigate(-1))}
      />
      {loading ? (
        <Skeleton className="h-40 w-full" />
      ) : !state ? (
        <EmptyState
          icon={<Factory />}
          title={<BilingualLabel k="process.cache.missingTitle" layout="stacked" />}
          description={t('process.cache.missingBody')}
          action={<SiteUpdateButton variant="primary" />}
        />
      ) : (
        <>
          <CacheAge downloadedAt={state.downloadedAt} />
          {children(state)}
        </>
      )}
    </div>
  )
}
